// bulbal-mention.js
// bulbal: thư viện THUẦN HÀM dựng/gỡ Drafty node "MN" (mention) cho ô soạn tin Bulbal Chat.
// Không React, không CSS, không side-effect — test được thuần Node (npm test).
//
// Vị trí đã đọc tại chỗ trong SDK cài sẵn (KHÔNG sửa, chỉ tham chiếu):
//   node_modules/tinode-sdk/umd/tinode.dev.js v0.25.3
//   :1508-1514  ENTITY_TYPES 'MN' — regex /\B@([\p{L}\p{N}][._\p{L}\p{N}]*[\p{L}\p{N}])/ug
//               KHÔNG khớp khoảng trắng ⇒ "@Nguyễn Văn A" chỉ bắt "@Nguyễn".
//               pack(val) = {val: val.slice(1)} ⇒ data.val là CHỮ người dùng gõ, KHÔNG PHẢI uid.
//   :1851-1966  Drafty.parse — quy đổi mọi fmt/ent sang chỉ số GRAPHEME qua toGraphemeValues().
//   :1968-2005  Drafty.append — remap fmt.key + gộp ent ĐÚNG CÁCH (hàm ghép an toàn duy nhất).
//   :2169-2183  Drafty.mention(name, uid) — len = stringToGraphemes(name).length (GRAPHEME).
//               ⚠️ BẪY: Drafty.appendLink (:2185), Drafty.wrapInto (:2253), Drafty.insertButton
//               (:2270) trong CÙNG SDK vẫn dùng .length UTF-16 thường ⇒ CẤM dùng 3 hàm đó để
//               dựng mention, sẽ lệch toạ độ với text có NFD/emoji.
//   :3171-3198  graphemeIndices() + toGraphemeValues() + stringToGraphemes() — thuật toán gốc
//               dùng Intl.Segmenter. File này tự dựng lại thuật toán tương đương (không import
//               nội bộ SDK vì bundle UMD không export các hàm private này).
//
// Lỗi đang sống mà thư viện này vá: Drafty.parse tự nhận diện "@word" thành node MN với
// data.val = CHỮ (không phải uid), trong khi formatters.js:117-123 lại dùng data.val như uid
// để tô màu ⇒ mọi @ gõ tay hiện tại tô màu nhưng trỏ vào hư không (uid giả). buildMentionDrafty
// gỡ SẠCH các node MN tự sinh này rồi mới chèn lại node MN thật (uid đã xác thực từ bảng gợi ý).

import { Drafty } from 'tinode-sdk';

// bulbal: uid ảo cho "@tất cả" — Tinode uid thật luôn có tiền tố "usr" nên không đụng độ.
export const MENTION_ALL_VAL = 'all';

let segmenterInstance = null;
function getSegmenter() {
  if (!segmenterInstance) {
    segmenterInstance = new Intl.Segmenter();
  }
  return segmenterInstance;
}

// bulbal: số grapheme cluster (KHÔNG phải code point, KHÔNG phải UTF-16 .length).
export function graphemeLength(str) {
  if (typeof str !== 'string' || str.length === 0) {
    return 0;
  }
  let count = 0;
  for (const _seg of getSegmenter().segment(str)) { // eslint-disable-line no-unused-vars
    count += 1;
  }
  return count;
}

// bulbal: bảng ánh xạ chỉ số UTF-16 -> chỉ số grapheme, tái hiện graphemeIndices() SDK :3171.
function graphemeIndexTable(str) {
  const table = [];
  let graphemeIdx = 0;
  let charIdx = 0;
  for (const { segment } of getSegmenter().segment(str)) {
    for (let i = 0; i < segment.length; i += 1) {
      table[charIdx + i] = graphemeIdx;
    }
    charIdx += segment.length;
    graphemeIdx += 1;
  }
  return table;
}

// bulbal: quy đổi {start,end} UTF-16 (giống toGraphemeValues() SDK :3186) -> {at,len} grapheme.
function utf16RangeToGrapheme(table, text, start, end) {
  const at = table[start];
  let len;
  if (end > start && end <= text.length) {
    len = table[end - 1] - at + 1;
  } else {
    len = end - start;
  }
  return { at, len };
}

// bulbal: R2 — chịu .trim() đầu/cuối, dịch toạ độ theo số ký tự trắng đầu bị cắt, loại vùng
// hỏng/ngoài biên/chồng lấn. Trả {text, ranges} đã sẵn sàng cho buildMentionDrafty.
export function normalizeRanges(text, ranges) {
  const raw = typeof text === 'string' ? text : '';
  const trimmed = raw.trim();
  const leadTrim = raw.length - raw.replace(/^\s+/, '').length;

  const cleaned = (ranges || [])
    .filter((r) => r && typeof r.start === 'number' && typeof r.end === 'number' && r.end > r.start)
    .map((r) => ({ start: r.start - leadTrim, end: r.end - leadTrim, uid: r.uid }))
    .filter((r) => r.start >= 0 && r.end <= trimmed.length)
    .sort((a, b) => a.start - b.start);

  const kept = [];
  let lastEnd = -1;
  cleaned.forEach((r) => {
    if (r.start >= lastEnd) {
      kept.push(r);
      lastEnd = r.end;
    }
    // else: chồng lấn vùng đã giữ trước đó -> loại (KISS: ưu tiên vùng đến trước)
  });

  return { text: trimmed, ranges: kept };
}

// bulbal: nén lại fmt/ent theo 1 predicate giữ-hay-bỏ entity; đánh số lại key cho fmt còn sống.
// Dùng chung cho bước "gỡ MN tự sinh" (buildMentionDrafty) và "gỡ MN không xác thực"
// (stripUnverifiedMentions) — DRY.
function compactFmtAndEnt(fmt, ent, keepEntity) {
  const oldFmt = fmt || [];
  const oldEnt = ent || [];
  const remap = new Map();
  const newEnt = [];

  oldEnt.forEach((e, i) => {
    if (keepEntity(e)) {
      remap.set(i, newEnt.length);
      newEnt.push(e);
    }
  });

  const newFmt = [];
  oldFmt.forEach((f) => {
    if (f.tp) {
      // fmt kiểu style (bold, BR...) không trỏ ent -> giữ nguyên.
      newFmt.push({ ...f });
      return;
    }
    const oldIdx = f.key || 0;
    if (remap.has(oldIdx)) {
      newFmt.push({ at: f.at, len: f.len, key: remap.get(oldIdx) });
    }
    // else: fmt trỏ ent đã bị loại (vd MN giả) -> bỏ theo.
  });

  return { fmt: newFmt, ent: newEnt };
}

// bulbal: dựng Drafty từ (chuỗi thô, danh sách vùng mention {start,end,uid} theo UTF-16 trong
// ô nhập). Xem thuật toán 7 bước ở phase-01. Trả object Drafty {txt,fmt,ent} hoặc chuỗi thuần
// nếu không còn fmt/ent nào (giữ hành vi cũ cho tin plain text).
export function buildMentionDrafty(text, ranges) {
  const { text: normText, ranges: normRanges } = normalizeRanges(text, ranges);

  let dft = Drafty.parse(normText);
  if (typeof dft === 'string') {
    dft = { txt: dft };
  }
  if (!dft || typeof dft.txt !== 'string') {
    dft = { txt: normText };
  }

  // bulbal: gỡ mọi MN do Drafty.parse tự sinh (data.val là CHỮ, xem chú thích đầu file).
  const { fmt, ent } = compactFmtAndEnt(dft.fmt, dft.ent, (e) => e.tp !== 'MN');

  const table = graphemeIndexTable(normText);
  normRanges.forEach(({ start, end, uid }) => {
    const { at, len } = utf16RangeToGrapheme(table, normText, start, end);
    if (typeof at !== 'number' || !(len > 0)) {
      return; // bulbal: toạ độ hỏng -> bỏ qua an toàn, không chèn mention rác
    }
    const key = ent.length;
    ent.push({ tp: 'MN', data: { val: uid } });
    fmt.push({ at, len, key });
  });

  if (fmt.length === 0) {
    return normText;
  }
  const result = { txt: normText, fmt };
  if (ent.length > 0) {
    result.ent = ent;
  }
  return result;
}

// bulbal: gỡ mọi entity MN mà data.val KHÔNG nằm trong allowedVals — dùng khi soạn lại/sửa tin
// để chống mention giả mạo (xem Security Considerations phase-01). Không đụng string đầu vào.
export function stripUnverifiedMentions(drafty, allowedVals) {
  if (typeof drafty === 'string' || !drafty) {
    return drafty;
  }
  const allowed = new Set(allowedVals || []);
  const { fmt, ent } = compactFmtAndEnt(
    drafty.fmt,
    drafty.ent,
    (e) => !(e.tp === 'MN' && !allowed.has(e.data && e.data.val))
  );

  if (fmt.length === 0) {
    return drafty.txt || '';
  }
  const result = { txt: drafty.txt, fmt };
  if (ent.length > 0) {
    result.ent = ent;
  }
  return result;
}
